/**
 * Safe Playwright Locator Chain Parser
 *
 * Parses and evaluates Playwright locator chain strings authored by the
 * cache selector agent. Only allows a strict allowlist of Playwright
 * locator methods — no evaluate(), no arbitrary JS execution.
 *
 * @module workflows/cache/selector-agent/locator-parser
 * @see docs/features/workflow-cache.md
 */

import type { Page, Locator } from 'playwright';
import { createLogger } from './logger.js';

let _logger: ReturnType<typeof createLogger> | null = null;
const getLogger = () => {
  if (!_logger) {
    _logger = createLogger({ module: 'LocatorParser' });
  }
  return _logger;
};

// ─────────────────────────────────────────────────────────────────────────────
// Allowlisted Playwright methods
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Methods that can be called on Page to produce a Locator.
 */
const PAGE_METHODS = new Set([
  'getByRole',
  'getByText',
  'getByLabel',
  'getByTestId',
  'getByPlaceholder',
  'locator',
]);

/**
 * Methods that can be chained on a Locator to refine it.
 */
const LOCATOR_METHODS = new Set([
  'getByRole',
  'getByText',
  'getByLabel',
  'getByTestId',
  'getByPlaceholder',
  'filter',
  'locator',
  'nth',
  'first',
  'last',
]);

/**
 * A parsed method call in a locator chain.
 */
interface LocatorMethodCall {
  method: string;
  args: unknown[];
}

const PRIVATE_USE_GLYPH_RE = /[\uE000-\uF8FF]/g;
const HAS_PRIVATE_USE_GLYPH_RE = /[\uE000-\uF8FF]/;

// ─────────────────────────────────────────────────────────────────────────────
// Variable Hydration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Replace {{var.key}} placeholders with actual values from the variable registry.
 */
/**
 * Escape a value for safe insertion into a single-quoted locator string context.
 * Backslashes and single quotes must be escaped to avoid breaking the locator syntax.
 */
function escapeForLocatorString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Replace {{var.key}} placeholders with actual values from the variable registry.
 * Values are escaped for the locator string context so that quotes and backslashes
 * in the variable value don't produce invalid locator syntax.
 */
export function hydrateLocatorVariables(
  locatorStr: string,
  variableValues: Record<string, string>
): string {
  return locatorStr.replace(/\{\{var\.([a-zA-Z0-9_]+)\}\}/g, (_match, key: string) => {
    const value = variableValues[key];
    if (value === undefined) {
      getLogger().warn('[LocatorParser] Variable placeholder not found in values', { key });
      return `{{var.${key}}}`;
    }
    return escapeForLocatorString(value);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Locator String Parser
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse a Playwright locator chain string into a sequence of method calls.
 *
 * Handles strings like:
 *   getByRole('row').filter({ hasText: 'Alpha' }).getByRole('button', { name: 'Edit' })
 *   getByText('Submit')
 *   locator('table').getByRole('row').nth(2)
 *
 * @throws Error if the string contains disallowed methods or is malformed
 */
export function parseLocatorChain(locatorStr: string): LocatorMethodCall[] {
  const calls: LocatorMethodCall[] = [];
  let remaining = locatorStr.trim();

  if (!remaining) {
    throw new Error('Empty locator string');
  }

  while (remaining.length > 0) {
    // Match method name
    const methodMatch = remaining.match(/^\.?([a-zA-Z]+)\s*\(/);
    if (!methodMatch) {
      throw new Error(`Invalid locator syntax at: "${remaining.slice(0, 50)}"`);
    }

    const method = methodMatch[1];

    // Validate method is allowlisted
    const isFirstCall = calls.length === 0;
    const allowedSet = isFirstCall ? PAGE_METHODS : LOCATOR_METHODS;
    if (!allowedSet.has(method)) {
      throw new Error(`Disallowed method "${method}". Allowed: ${[...allowedSet].join(', ')}`);
    }

    // Find the matching closing paren, handling nested parens and strings
    const argsStart = methodMatch[0].length;
    remaining = remaining.slice(argsStart);
    const { args, rest } = extractArgs(remaining);

    calls.push({ method, args });
    remaining = rest.trim();

    // Handle .first() and .last() — no-arg chainable
    if (remaining.startsWith('.')) {
      const noArgMatch = remaining.match(/^\.(first|last)\b(?!\s*\()/);
      if (noArgMatch) {
        const noArgMethod = noArgMatch[1];
        if (!LOCATOR_METHODS.has(noArgMethod)) {
          throw new Error(`Disallowed method "${noArgMethod}"`);
        }
        calls.push({ method: noArgMethod, args: [] });
        remaining = remaining.slice(noArgMatch[0].length).trim();
      }
    }
  }

  if (calls.length === 0) {
    throw new Error('No valid method calls found in locator string');
  }

  return calls;
}

/**
 * Extract arguments from a parenthesized argument list.
 * Returns parsed args and the remaining string after the closing paren.
 */
function extractArgs(input: string): { args: unknown[]; rest: string } {
  let depth = 1;
  let i = 0;
  let inString: "'" | '"' | '`' | null = null;
  let escaped = false;

  while (i < input.length && depth > 0) {
    const ch = input[i];

    if (escaped) {
      escaped = false;
      i++;
      continue;
    }

    if (ch === '\\') {
      escaped = true;
      i++;
      continue;
    }

    if (inString) {
      if (ch === inString) {
        inString = null;
      }
    } else {
      if (ch === "'" || ch === '"' || ch === '`') {
        inString = ch;
      } else if (ch === '(') {
        depth++;
      } else if (ch === ')') {
        depth--;
        if (depth === 0) break;
      }
    }

    i++;
  }

  if (depth !== 0) {
    throw new Error('Unmatched parenthesis in locator string');
  }

  const argsStr = input.slice(0, i).trim();
  const rest = input.slice(i + 1); // skip closing paren

  if (!argsStr) {
    return { args: [], rest };
  }

  // Parse the argument string as JSON-compatible values
  const args = parseArgsList(argsStr);
  return { args, rest };
}

/**
 * Parse a comma-separated argument list into JS values.
 * Handles: strings, numbers, booleans, objects (like { hasText: 'foo', name: 'bar' })
 */
function parseArgsList(argsStr: string): unknown[] {
  const args: unknown[] = [];
  let remaining = argsStr.trim();

  while (remaining.length > 0) {
    remaining = remaining.trim();
    if (remaining.startsWith(',')) {
      remaining = remaining.slice(1).trim();
    }
    if (!remaining) break;

    if (remaining.startsWith('{')) {
      // Object argument
      const { value, rest } = parseObjectArg(remaining);
      args.push(value);
      remaining = rest;
    } else if (remaining.startsWith("'") || remaining.startsWith('"')) {
      // String argument
      const quote = remaining[0];
      let end = 1;
      while (end < remaining.length) {
        if (remaining[end] === '\\') {
          end += 2;
          continue;
        }
        if (remaining[end] === quote) break;
        end++;
      }
      const str = remaining.slice(1, end).replace(/\\'/g, "'").replace(/\\"/g, '"');
      args.push(str);
      remaining = remaining.slice(end + 1).trim();
    } else if (/^-?\d/.test(remaining)) {
      // Number argument
      const numMatch = remaining.match(/^-?\d+/);
      if (numMatch) {
        args.push(parseInt(numMatch[0], 10));
        remaining = remaining.slice(numMatch[0].length).trim();
      }
    } else if (remaining.startsWith('true')) {
      args.push(true);
      remaining = remaining.slice(4).trim();
    } else if (remaining.startsWith('false')) {
      args.push(false);
      remaining = remaining.slice(5).trim();
    } else if (remaining.startsWith('/')) {
      // RegExp argument — parse /pattern/flags
      const regexMatch = remaining.match(/^\/([^/]+)\/([gimsuy]*)/);
      if (regexMatch) {
        args.push(new RegExp(regexMatch[1], regexMatch[2]));
        remaining = remaining.slice(regexMatch[0].length).trim();
      } else {
        throw new Error(`Invalid argument at: "${remaining.slice(0, 30)}"`);
      }
    } else {
      throw new Error(`Invalid argument at: "${remaining.slice(0, 30)}"`);
    }
  }

  return args;
}

/**
 * Parse a JS-like object literal: { key: value, key2: value2 }
 */
function parseObjectArg(input: string): { value: Record<string, unknown>; rest: string } {
  let depth = 1;
  let i = 1; // skip opening {
  let inString: "'" | '"' | null = null;

  while (i < input.length && depth > 0) {
    const ch = input[i];
    if (inString) {
      if (ch === '\\') {
        i += 2;
        continue;
      }
      if (ch === inString) inString = null;
    } else {
      if (ch === "'" || ch === '"') inString = ch;
      else if (ch === '{') depth++;
      else if (ch === '}') depth--;
    }
    if (depth > 0) i++;
  }

  const objStr = input.slice(1, i).trim();
  const rest = input.slice(i + 1);

  // Parse key-value pairs
  const obj: Record<string, unknown> = {};
  const pairs = splitObjectPairs(objStr);

  for (const pair of pairs) {
    const colonIdx = pair.indexOf(':');
    if (colonIdx === -1) continue;

    const key = pair
      .slice(0, colonIdx)
      .trim()
      .replace(/^['"]|['"]$/g, '');
    const rawValue = pair.slice(colonIdx + 1).trim();

    if (rawValue.startsWith("'") || rawValue.startsWith('"')) {
      const q = rawValue[0];
      let end = 1;
      while (end < rawValue.length && rawValue[end] !== q) {
        if (rawValue[end] === '\\') end++;
        end++;
      }
      obj[key] = rawValue.slice(1, end).replace(/\\'/g, "'").replace(/\\"/g, '"');
    } else if (rawValue.startsWith('/')) {
      const regexMatch = rawValue.match(/^\/([^/]+)\/([gimsuy]*)/);
      if (regexMatch) {
        obj[key] = new RegExp(regexMatch[1], regexMatch[2]);
      }
    } else if (rawValue === 'true') {
      obj[key] = true;
    } else if (rawValue === 'false') {
      obj[key] = false;
    } else if (/^-?\d+$/.test(rawValue)) {
      obj[key] = parseInt(rawValue, 10);
    } else {
      obj[key] = rawValue;
    }
  }

  return { value: obj, rest };
}

/**
 * Split object key-value pairs by commas, respecting nested strings.
 */
function splitObjectPairs(objStr: string): string[] {
  const pairs: string[] = [];
  let current = '';
  let inString: "'" | '"' | null = null;

  for (let i = 0; i < objStr.length; i++) {
    const ch = objStr[i];
    if (inString) {
      if (ch === '\\') {
        current += ch + (objStr[i + 1] ?? '');
        i++;
        continue;
      }
      if (ch === inString) inString = null;
    } else {
      if (ch === "'" || ch === '"') inString = ch;
      else if (ch === ',') {
        if (current.trim()) pairs.push(current.trim());
        current = '';
        continue;
      }
    }
    current += ch;
  }

  if (current.trim()) pairs.push(current.trim());
  return pairs;
}

// ─────────────────────────────────────────────────────────────────────────────
// Locator Chain Evaluator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate a parsed locator chain against a Playwright Page, returning a Locator.
 *
 * @param page - Playwright Page to evaluate against
 * @param calls - Parsed method calls from parseLocatorChain()
 * @returns Playwright Locator
 */
function evaluateLocatorCalls(page: Page, calls: LocatorMethodCall[]): Locator {
  if (calls.length === 0) {
    throw new Error('Empty locator chain');
  }

  // First call is on the Page
  const firstCall = calls[0];
  let locator = callMethod(page, firstCall.method, firstCall.args) as Locator;

  // Subsequent calls are on the Locator
  for (let i = 1; i < calls.length; i++) {
    locator = callMethod(locator, calls[i].method, calls[i].args) as Locator;
  }

  return locator;
}

/**
 * Parse, hydrate variables, and evaluate a locator chain string.
 * This is the main entry point for resolving agentic locators.
 *
 * @param page - Playwright Page
 * @param locatorStr - Raw locator chain string (may contain {{var.key}} placeholders)
 * @param variableValues - Variable values for placeholder hydration
 * @returns Playwright Locator
 */
export function evaluateLocatorChain(
  page: Page,
  locatorStr: string,
  variableValues?: Record<string, string>
): Locator {
  const hydrated = variableValues
    ? hydrateLocatorVariables(locatorStr, variableValues)
    : locatorStr;
  const calls = parseLocatorChain(hydrated);
  return evaluateLocatorCalls(page, calls);
}

/**
 * Call a method on a Page or Locator with the given args.
 * Only allows methods from the allowlist.
 */
function callMethod(target: Page | Locator, method: string, args: unknown[]): Locator {
  const fn = (target as unknown as Record<string, unknown>)[method];
  if (typeof fn !== 'function') {
    throw new Error(`Method "${method}" not found on target`);
  }
  return Reflect.apply(fn, target, normalizeLocatorArgsForIconGlyphs(method, args)) as Locator;
}

export function normalizeLocatorArgsForIconGlyphs(method: string, args: unknown[]): unknown[] {
  if (method === 'getByRole') {
    const [role, options, ...rest] = args;
    if (!isRecord(options)) return args;
    return [
      role,
      {
        ...options,
        name: iconGlyphTolerantPattern(options.name),
      },
      ...rest,
    ];
  }

  if (method === 'filter') {
    const [options, ...rest] = args;
    if (!isRecord(options)) return args;
    return [
      {
        ...options,
        hasText: iconGlyphTolerantPattern(options.hasText),
      },
      ...rest,
    ];
  }

  if (method === 'getByText' || method === 'getByLabel') {
    const [text, ...rest] = args;
    return [iconGlyphTolerantPattern(text), ...rest];
  }

  return args;
}

function iconGlyphTolerantPattern(value: unknown): unknown {
  if (typeof value !== 'string' || !HAS_PRIVATE_USE_GLYPH_RE.test(value)) return value;

  const readable = value.replace(PRIVATE_USE_GLYPH_RE, ' ').replace(/\s+/g, ' ').trim();
  if (!readable) return value;

  const tokens = readable.split(/\s+/).map(escapeRegex);
  return new RegExp(tokens.join('[\\s\\uE000-\\uF8FF]+'), 'i');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Strip `.filter({ ... })` clauses from a Playwright locator chain string.
 * Returns the stripped string, or null if no `.filter(` was found.
 *
 * Example:
 *   "getByRole('dialog').getByRole('combobox').filter({ hasText: 'foo' })"
 *   → "getByRole('dialog').getByRole('combobox')"
 */
export function stripFilterClauses(locatorStr: string): string | null {
  // Match .filter( followed by balanced parens
  const filterPattern = /\.filter\s*\(/g;
  let match: RegExpExecArray | null;
  let result = locatorStr;
  let found = false;

  // Process from right to left to preserve indices
  const matches: { start: number; end: number }[] = [];
  while ((match = filterPattern.exec(locatorStr)) !== null) {
    // Find the matching closing paren
    const argsStart = match.index + match[0].length;
    let depth = 1;
    let i = argsStart;
    let inString: "'" | '"' | '`' | null = null;
    let escaped = false;

    while (i < locatorStr.length && depth > 0) {
      const ch = locatorStr[i];
      if (escaped) {
        escaped = false;
        i++;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        i++;
        continue;
      }
      if (inString) {
        if (ch === inString) inString = null;
      } else {
        if (ch === "'" || ch === '"' || ch === '`') inString = ch;
        else if (ch === '(') depth++;
        else if (ch === ')') {
          depth--;
          if (depth === 0) break;
        }
      }
      i++;
    }

    if (depth === 0) {
      matches.push({ start: match.index, end: i + 1 });
      found = true;
    }
  }

  if (!found) return null;

  // Remove matches from right to left
  for (let j = matches.length - 1; j >= 0; j--) {
    const { start, end } = matches[j];
    result = result.slice(0, start) + result.slice(end);
  }

  // Trim trailing dots
  result = result.replace(/\.$/, '');

  return result || null;
}

/**
 * Validate that a locator string only uses allowed methods.
 * Returns true if valid, throws Error with details if not.
 */
export function validateLocatorString(locatorStr: string): boolean {
  parseLocatorChain(locatorStr); // throws on invalid
  return true;
}
